import React, { forwardRef, useEffect } from 'react';
import CancellationRule from '../../interfaces/output/cancellationRule';
import LanguageUtils from '../../utils/LanguageUtils';
import PageUtils from '../../utils/pageUtils';
import { useDispatch, useSelector } from 'react-redux';
import WorkflowUtils from '../../utils/workflowUtils';
import Loader from '../common/widgets/loader';
import { Checkbox, FormControl, FormControlLabel, Grid } from '@mui/material';
import { fetchCancellationTypes, getCancellationTypes } from '../../reducers/cancellationTypeReducer';
import { fetchDepartureTypes, getDepartureTypes } from '../../reducers/departureTypeReducer';
import { fetchPriorityTypes, getPriorityTypes } from '../../reducers/priorityTypeReducer';
import { fetchTerminals, getTerminals } from '../../reducers/terminalReducer';
import { fetchUnitTypes, getUnitTypes } from '../../reducers/unitTypeReducer';
import GenericAutocomplete from '../common/widgets/genericAutocomplete';
import Terminal from '../../interfaces/output/terminal';
import CancellationType from '../../interfaces/output/cancellationType';
import PriorityType from '../../interfaces/output/priorityType';
import UnitType from '../../interfaces/output/unitType';
import DepartureType from '../../interfaces/output/departureType';
import GenericTextField from '../common/widgets/genericTextField';
import { FormattedMessage } from 'react-intl';
import { isLoadingCancellationRuleById } from '../../reducers/cancellationRuleSetReducer';
import CancellationRuleStep1Validation from './validations/cancellationRuleValidation';

interface ICancellationRuleSetStep1Props {
    cancellationRule: CancellationRule;
    onChange: any;
    showValidationError?: boolean
}

const ATTRIBUTES = {
    PRIORITY_RANK: 'priorityRank',
    PRIORITY_TYPE: 'priorityType',
    FROM_LOADING_HOURS: 'fromLoadingHours',
    TO_LOADING_HOURS: 'toLoadingHours',
    TO_DAYS_BEFORE_LOADING_DATE: 'toDaysBeforeLoadingDate',
    FROM_HOUR_LIMIT: 'fromHourLimit',
    TO_HOUR_LIMIT: 'toHourLimit',
    CANCELLATION_TYPE: 'cancellationType',
    TO_LOADING_MONTH_DAY: 'toLoadingMonthDay',
    FROM_LOADING_MONTH_DAY: 'fromLoadingMonthDay'

};

const messages = {
    priorityRank: LanguageUtils.createMessage('Priority rank'),
    fromTerminal: LanguageUtils.createMessage('Terminal from'),
    toTerminal: LanguageUtils.createMessage('Terminal to'),
    unitType: LanguageUtils.createMessage('Unit type'),
    priorityType: LanguageUtils.createMessage('Priority type'),
    loadingWeekday: LanguageUtils.createMessage('Loading weekday'),
    fromLoadingMonthDay: LanguageUtils.createMessage('From loading month day'),
    toLoadingMonthDay: LanguageUtils.createMessage('To loading month day'),
    loadingTrainNumber: LanguageUtils.createMessage('Loading train number'),
    fromLoadingHours: LanguageUtils.createMessage('From loading hours'),
    toLoadingHours: LanguageUtils.createMessage('To loading hours'),
    departureType: LanguageUtils.createMessage('Departure type'),
    cancellationMinutesBeforeLoading: LanguageUtils.createMessage('Cancellation minutes befor loading'),
    fromDaysBeforeLoadingDate: LanguageUtils.createMessage('From days before loading date'),
    fromHourLimit: LanguageUtils.createMessage('From hour limit'),
    toDaysBeforeLoadingDate: LanguageUtils.createMessage('To days before loading date'),
    toHourLimit: LanguageUtils.createMessage('To hour limit'),
    cancellationType: LanguageUtils.createMessage('Cancellation type')
};

const CancellationRuleSetFormStep1 = forwardRef((props: ICancellationRuleSetStep1Props, ref: any): JSX.Element => {
    const { cancellationRule, onChange, showValidationError } = props;
    const paging = PageUtils.getMaxPaging();
    WorkflowUtils.setHandle(ref, null);
    const dispatch = useDispatch();
    const cancellationTypes = useSelector(getCancellationTypes).content;
    const priorityTypes = useSelector(getPriorityTypes).content;
    const unitTypes = useSelector(getUnitTypes).content;
    const departureTypes = useSelector(getDepartureTypes).content;
    const terminals = useSelector(getTerminals).content;

    const isLoadingObjects = [useSelector(isLoadingCancellationRuleById)];
    const isLoading = isLoadingObjects.find((obj: boolean) => obj === true);

    useEffect(() => {
        dispatch(fetchCancellationTypes({ paging }));
        dispatch(fetchDepartureTypes({ paging }));
        dispatch(fetchPriorityTypes({ paging }));
        dispatch(fetchTerminals({ paging }));
        dispatch(fetchUnitTypes({ paging }));
    }, [dispatch]);

    const setValidationState = (attribute: string) => {
        let isValid = true;

        if(!showValidationError || !attribute) {
            return undefined;
        }

        switch (attribute) {

            case ATTRIBUTES.PRIORITY_RANK: {
                isValid = CancellationRuleStep1Validation.validateInputNumber(cancellationRule.priorityRank);
                break;
            }
            case ATTRIBUTES.PRIORITY_TYPE: {
                isValid = CancellationRuleStep1Validation.validateInputNumber(cancellationRule.priorityType?.id);
                break;
            }
            case ATTRIBUTES.TO_DAYS_BEFORE_LOADING_DATE: {
                isValid = CancellationRuleStep1Validation.validateInputNumber(cancellationRule.toDaysBeforeLoadingDate);
                break;
            }
            case ATTRIBUTES.FROM_LOADING_HOURS: {
                isValid = CancellationRuleStep1Validation.validClock(cancellationRule.fromLoadingHours) &&
                    CancellationRuleStep1Validation.validateInputString(cancellationRule.fromLoadingHours);
                break;
            }
            case ATTRIBUTES.TO_LOADING_HOURS: {
                isValid = CancellationRuleStep1Validation.validClock(cancellationRule.toLoadingHours) &&
                    CancellationRuleStep1Validation.validateInputString(cancellationRule.toLoadingHours);
                break;
            }
            case ATTRIBUTES.FROM_HOUR_LIMIT: {
                isValid = CancellationRuleStep1Validation.validClock(cancellationRule.fromHourLimit);
                break;
            }
            case ATTRIBUTES.TO_HOUR_LIMIT: {
                isValid = CancellationRuleStep1Validation.validClock(cancellationRule.toHourLimit) &&
                    CancellationRuleStep1Validation.validateInputString(cancellationRule.toHourLimit);
                break;
            }
            case ATTRIBUTES.CANCELLATION_TYPE: {
                isValid = CancellationRuleStep1Validation.validateInputNumber(cancellationRule.cancellationType?.id);
                break;
            }
            case ATTRIBUTES.FROM_LOADING_MONTH_DAY: {
                isValid = !cancellationRule.fromLoadingMonthDay ? true : CancellationRuleStep1Validation.numberIsInInterval(cancellationRule.fromLoadingMonthDay, 1, 31);
                break;
            }
            case ATTRIBUTES.TO_LOADING_MONTH_DAY: {
                isValid = !cancellationRule.toLoadingMonthDay ? true : CancellationRuleStep1Validation.numberIsInInterval(cancellationRule.toLoadingMonthDay, 1, 31);
                break;
            }
        }

        return !isValid;
    };

    return (
        <Loader isLoading={isLoading}>
            <Grid container spacing={2} mt={2}>
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="priorityRank"
                        label={messages.priorityRank}
                        value={cancellationRule.priorityRank}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.PRIORITY_RANK)}
                        required
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<Terminal>
                            options={terminals}
                            value={cancellationRule.fromTerminal}
                            onChange={(obj: Terminal | null) => onChange('fromTerminal', obj)}
                            placeholder={messages.fromTerminal}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<Terminal>
                            options={terminals}
                            value={cancellationRule?.toTerminal}
                            onChange={(obj: Terminal | null) => onChange('toTerminal', obj)}
                            placeholder={messages.toTerminal}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<UnitType>
                            options={unitTypes}
                            value={cancellationRule.unitType}
                            onChange={(obj: UnitType | null) => onChange('unitType', obj)}
                            placeholder={messages.unitType}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<PriorityType>
                            options={priorityTypes}
                            value={cancellationRule?.priorityType}
                            onChange={(obj: PriorityType | null) => onChange('priorityType', obj)}
                            placeholder={messages.priorityType}
                            error={setValidationState(ATTRIBUTES.PRIORITY_TYPE)}
                            required
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <FormControlLabel
                            control={<Checkbox checked={!!cancellationRule.loadingWeekday}
                                onChange={(e: any, value: boolean) => onChange('loadingWeekday', value)}
                            />}
                            label={<FormattedMessage {...messages.loadingWeekday} />}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="fromLoadingMonthDay"
                        label={messages.fromLoadingMonthDay}
                        value={cancellationRule.fromLoadingMonthDay}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.FROM_LOADING_MONTH_DAY)}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="toLoadingMonthDay"
                        label={messages.toLoadingMonthDay}
                        value={cancellationRule.toLoadingMonthDay}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.TO_LOADING_MONTH_DAY)}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<string>
                        type="string"
                        name="loadingTrainNo"
                        label={messages.loadingTrainNumber}
                        value={cancellationRule.loadingTrainNo}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<string>
                        type="string"
                        name="fromLoadingHours"
                        label={messages.fromLoadingHours}
                        value={cancellationRule.fromLoadingHours}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.FROM_LOADING_HOURS)}
                        required
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<string>
                        type="string"
                        name="toLoadingHours"
                        label={messages.toLoadingHours}
                        value={cancellationRule.toLoadingHours}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.TO_LOADING_HOURS)}
                        required
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<DepartureType>
                            options={departureTypes}
                            value={cancellationRule.departureType}
                            onChange={(obj: DepartureType | null) => onChange('departureType', obj)}
                            placeholder={messages.departureType}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="cancellationMinutesBeforeLoading"
                        label={messages.cancellationMinutesBeforeLoading}
                        value={cancellationRule.cancellationMinutesBeforeLoading}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="fromDaysBeforeLoadingDate"
                        label={messages.fromDaysBeforeLoadingDate}
                        value={cancellationRule.fromDaysBeforeLoadingDate}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<string>
                        type="string"
                        name="fromHourLimit"
                        label={messages.fromHourLimit}
                        value={cancellationRule.fromHourLimit}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.FROM_HOUR_LIMIT)}
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<number>
                        type="number"
                        name="toDaysBeforeLoadingDate"
                        label={messages.toDaysBeforeLoadingDate}
                        value={cancellationRule.toDaysBeforeLoadingDate}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.TO_DAYS_BEFORE_LOADING_DATE)}
                        required
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <GenericTextField<string>
                        type="string"
                        name="toHourLimit"
                        label={messages.toHourLimit}
                        value={cancellationRule.toHourLimit}
                        onChange={onChange}
                        error={setValidationState(ATTRIBUTES.TO_HOUR_LIMIT)}
                        required
                    />
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericAutocomplete<CancellationType>
                            options={cancellationTypes}
                            value={cancellationRule?.cancellationType}
                            onChange={(obj: CancellationType | null) => onChange('cancellationType', obj)}
                            placeholder={messages.cancellationType}
                            error={setValidationState(ATTRIBUTES.CANCELLATION_TYPE)}
                            required
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
            </Grid>
        </Loader >
    );
});

export default CancellationRuleSetFormStep1;
